import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Stack, Typography} from "@mui/material";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";

import styles from "./FilmLists.module.css";
import {CustomTabs, CardSlider, Loader} from "../index";
import {IContent, IData, PaginationParams} from "../Cards/types";
import {routes} from "../../routes/routes";
import {Arrow} from "../../assets/files";
import useResize from "../../hooks/useResize";
import {
    useFetchCategoriesRequestQuery,
    useGetPageQuery,
    useLazyGetNearbyEventsQuery,
    useLazyGetPageContentQuery,
    useLazyGetGenreContentQuery,
} from "../../api/modules";
import {useUserRole} from "../../hooks/useUserRole";
import Keys from "../../constants/helper";
import {IContentContext} from "../../store/player/playerSlice";
import {ProgramPages} from "../../types";
import {dedupeContentsById} from "../../utils";
import {SPORTS_CATEGORIES_ICONS_BY_NAME, SPORTS_LIVE_NOW_CATEGORY_ID, SPORTS_LIVE_NOW_STATUS} from "../../constants";

type CategoryPageInfo = Record<string, {page: number; hasMore: boolean}>;

interface FilmListsProps {
    dataContent?: any[];
    allContents?: any[][];
    setAllContents?: React.Dispatch<React.SetStateAction<any[][]>>;
    categoryPageInfo?: Record<string | number, {page: number; hasMore: boolean}>;
    setCategoryPageInfo?: React.Dispatch<React.SetStateAction<any>>;
    pageName: string;
    refresh?: () => void;
    canRefresh?: boolean;
    isProgram?: boolean;
    isWithoutTabs?: boolean;
    isCategory?: boolean;
    isLoading?: boolean;
    paginationParams?: PaginationParams;
}

const MOBILE_BREAKPOINT = 860;
const TOP_10_LABEL = "Top 10";
const INITIAL_TAB = 0;

const FilmLists: React.FC<FilmListsProps> = ({
                                                 dataContent: dataList,
                                                 allContents = [],
                                                 setAllContents,
                                                 categoryPageInfo = {},
                                                 setCategoryPageInfo,
                                                 pageName,
                                                 refresh,
                                                 canRefresh = false,
                                                 isProgram = false,
                                                 isWithoutTabs = false,
                                                 isCategory = false,
                                                 paginationParams
                                             }) => {
    const {t} = useTranslation("translations");
    const {innerWidth} = useResize();
    const navigate = useNavigate();
    const role = useUserRole();

    const profileID = localStorage.getItem(Keys.EPIC_PROFILE_ID) as string;

    const activeRequestId = useRef(0);
    const loadingMoreCategoryRef = useRef<string | null>(null);
    const categoryPageInfoRef = useRef(categoryPageInfo);
    const requestedPageKeysRef = useRef<Set<string>>(new Set());

    const [dataContent, setDataContent] = useState<any[]>(dataList || []);
    const [selected, setSelected] = useState<number>(INITIAL_TAB);
    const [isTabLoading, setIsTabLoading] = useState(false);
    const [loadingMoreCategoryId, setLoadingMoreCategoryId] = useState<string | null>(null);
    const [genreContents, setGenreContents] = useState<any[]>([]);

    const categoryType = useMemo(() => (isCategory ? "category_list" : ""), [isCategory]);
    const isDesktop = useMemo(() => innerWidth > MOBILE_BREAKPOINT, [innerWidth]);
    const isSportsPage = pageName === ProgramPages.SPORTS;

    const {
        data: regularList,
        isLoading: isRegularListLoading,
        isFetching: isRegularListFetching,
    } = useGetPageQuery({page: pageName, role, type: categoryType, ...paginationParams}, {skip: isProgram});

    const {
        data: programList,
        isLoading: isProgramListLoading,
        isFetching: isProgramListFetching,
    } = useFetchCategoriesRequestQuery(
        {page: pageName, type: "category_list", role},
        {skip: !isProgram}
    );

    const [getGenreContent] = useLazyGetGenreContentQuery();
    const [getPageContent] = useLazyGetPageContentQuery();
    const [getNearbyEvents] = useLazyGetNearbyEventsQuery();

    const dataLists = useMemo(
        () => (isProgram ? programList : regularList),
        [isProgram, programList, regularList]
    );
    const tabsList = useMemo(
        () => (dataList?.length ? dataList : dataLists),
        [dataList, dataLists]
    );
    const visibleTabsList = useMemo(
        () => tabsList?.filter((category) => !category.isVirtual) ?? [],
        [tabsList]
    );

    const isInitialLoading = useMemo(
        () => (isProgram ? isProgramListLoading : isRegularListLoading),
        [isProgram, isProgramListLoading, isRegularListLoading]
    );

    const isListsFetching = useMemo(
        () => (isProgram ? isProgramListFetching : isRegularListFetching),
        [isProgram, isProgramListFetching, isRegularListFetching]
    );

    useEffect(() => {
        categoryPageInfoRef.current = categoryPageInfo;
    }, [categoryPageInfo]);

    useEffect(() => {
        if (selected !== INITIAL_TAB) return;

        if (dataList) setDataContent(dataList);
        setGenreContents([]);
    }, [selected, dataList]);

    const fetchGenreContent = useCallback(() => {
        if (selected === INITIAL_TAB) return;

        const requestId = ++activeRequestId.current;
        setIsTabLoading(true);

        const promise = getGenreContent({id: selected, profileID, role});
        promise
            .unwrap()
            .then((response) => {
                if (requestId !== activeRequestId.current) return;

                const filteredCategory = dataList?.filter((category) => category.id === selected) || [];
                setDataContent(filteredCategory);
                setGenreContents(response?.results ?? []);
            })
            .catch((error) => {
                if (requestId === activeRequestId.current) {
                    console.error("Error fetching genre content:", error);
                }
            })
            .finally(() => {
                if (requestId === activeRequestId.current) {
                    setIsTabLoading(false);
                }
            });

        return () => {
            promise.abort?.();
        };
    }, [selected, getGenreContent, profileID, dataList]);

    useEffect(() => {
        return fetchGenreContent();
    }, [fetchGenreContent]);

    /**
     * Build UI data:
     * - All tab uses allContents[index] (or fallback category.contents)
     * - Genre tab uses genreContents in index 0
     */
    const mergedData = useMemo(() => {
        if (!dataContent.length) return [];

        return dataContent.map((category, index) => {
            const contents =
                selected === INITIAL_TAB
                    ? dedupeContentsById(allContents[index] || category.contents || [])
                    : index === 0
                        ? dedupeContentsById(genreContents || [])
                        : [];

            return {...category, contents};
        });
    }, [dataContent, allContents, genreContents, selected]);

    const handleUpdate = useCallback(() => {
        if (canRefresh && refresh) refresh();
    }, [refresh, canRefresh]);

    const handleViewMore = useCallback(
        (categoryId: string | number, categoryName: string) => {
            navigate(`${routes.homeCategories}/${categoryId}`, {
                state: {isProgram, name: categoryName},
            });
        },
        [navigate, isProgram]
    );

    const handleLoadMoreCategory = useCallback(
        async (category: IContent, index: number) => {
            if (selected !== INITIAL_TAB || !setAllContents || !setCategoryPageInfo) return;

            const categoryId = String(category.id);
            const pageInfo = categoryPageInfoRef.current[categoryId];
            if (!pageInfo?.hasMore || loadingMoreCategoryRef.current === categoryId) return;

            const nextPage = pageInfo.page + 1;
            const requestPageKey = `${categoryId}:${nextPage}`;
            if (requestedPageKeysRef.current.has(requestPageKey)) return;

            requestedPageKeysRef.current.add(requestPageKey);
            loadingMoreCategoryRef.current = categoryId;
            setLoadingMoreCategoryId(categoryId);

            const optimisticPageInfo = {
                ...categoryPageInfoRef.current,
                [categoryId]: {
                    page: nextPage,
                    hasMore: false,
                },
            };
            categoryPageInfoRef.current = optimisticPageInfo;
            setCategoryPageInfo(optimisticPageInfo);

            try {
                const isLiveNowCategory = categoryId === SPORTS_LIVE_NOW_CATEGORY_ID;
                const response = await (
                    isLiveNowCategory
                        ? getNearbyEvents({
                            page_number: nextPage,
                            rehydrated_pagination: true,
                            status: SPORTS_LIVE_NOW_STATUS,
                        })
                        : getPageContent({
                            page: pageName,
                            role,
                            content_id: Number(categoryId),
                            profile_id: Number(profileID),
                            page_number: nextPage,
                            rehydrated_pagination: true,
                        })
                ).unwrap();

                setAllContents((prev) => {
                    const nextContents = [...prev];
                    const currentCategoryContents = nextContents[index] ?? category.contents ?? [];
                    nextContents[index] = dedupeContentsById([
                        ...currentCategoryContents,
                        ...(response?.results ?? []),
                    ]);
                    return nextContents;
                });

                const resolvedPageInfo: CategoryPageInfo = {
                    ...categoryPageInfoRef.current,
                    [categoryId]: {
                        page: nextPage,
                        hasMore: Boolean(response?.next),
                    },
                };
                categoryPageInfoRef.current = resolvedPageInfo;
                setCategoryPageInfo(resolvedPageInfo);
            } catch (error) {
                requestedPageKeysRef.current.delete(requestPageKey);
                const restoredPageInfo: CategoryPageInfo = {
                    ...categoryPageInfoRef.current,
                    [categoryId]: pageInfo,
                };
                categoryPageInfoRef.current = restoredPageInfo;
                setCategoryPageInfo(restoredPageInfo);
            } finally {
                loadingMoreCategoryRef.current = null;
                setLoadingMoreCategoryId(null);
            }
        },
        [
            selected,
            setAllContents,
            setCategoryPageInfo,
            getPageContent,
            getNearbyEvents,
            pageName,
            role,
            profileID,
        ]
    );

    const renderCategorySection = useCallback(
        (category: IContent, index: number) => {
            if (!category.contents?.length) return null;

            const isTop10 = category.name === TOP_10_LABEL;
            const isLiveNowCategory = category.id === SPORTS_LIVE_NOW_CATEGORY_ID;
            const isProgramContextPage =
                isProgram &&
                (
                    isSportsPage ||
                    pageName === ProgramPages.LIVE ||
                    pageName === ProgramPages.ADULT
                );
            const contentContext: IContentContext | null = isProgramContextPage
                ? {
                    page: pageName as IContentContext["page"],
                    categoryId: isLiveNowCategory ? 0 : Number(category.id),
                }
                : null;

            return (
                <div
                    key={category.id ?? `category-${index}`}
                    className={isLiveNowCategory ? styles.liveNowSection : undefined}
                >
                    <Stack className={styles.row} direction="row" spacing={1}>
                        <div className={isLiveNowCategory ? styles.liveNowTitleWrap : undefined}>
                            {isLiveNowCategory && <span className={styles.liveNowDot}/>}
                            <Typography variant="subtitle2">{category.name}</Typography>
                            {isLiveNowCategory && (
                                <Typography variant="caption" className={styles.liveNowSubtitle}>
                                    {t("Sports.liveNowSubtitle")}
                                </Typography>
                            )}
                        </div>

                        {isDesktop && !isTop10 && !isLiveNowCategory && (
                            <Typography
                                variant="subtitle2"
                                onClick={() => handleViewMore(category.id, category.name)}
                                className={styles.viewMore}
                            >
                                {t("Buttons.view_more")}
                                <Arrow/>
                            </Typography>
                        )}
                    </Stack>

                    <CardSlider
                        data={category.contents}
                        isProgram={isProgram}
                        top10={isTop10}
                        contentContext={contentContext}
                        hasMore={Boolean(categoryPageInfo[String(category.id)]?.hasMore)}
                        isLoadingMore={loadingMoreCategoryId === String(category.id)}
                        onLoadMore={() => handleLoadMoreCategory(category, index)}
                        isSportsCard={isSportsPage}
                    />
                </div>
            );
        },
        [
            isDesktop,
            handleViewMore,
            isProgram,
            pageName,
            t,
            categoryPageInfo,
            loadingMoreCategoryId,
            handleLoadMoreCategory,
        ]
    );

    if (isInitialLoading) {
        return (
            <div className={styles.root}>
                <Loader className={styles.loader}/>
            </div>
        );
    }

    return (
        <div className={styles.root}>
            {tabsList && (
                <div className={styles.box}>
                    {!isWithoutTabs && (
                        <CustomTabs
                            list={visibleTabsList}
                            categories
                            setSelected={setSelected}
                            onFetch={handleUpdate}
                            className={isSportsPage ? styles.sportsCategoriesTabs : undefined}
                            categoryIconsByName={isSportsPage ? SPORTS_CATEGORIES_ICONS_BY_NAME : undefined}
                        />
                    )}

                    {isTabLoading ? (
                        <div className={styles.tabLoaderWrap}>
                            <Loader className={styles.loader}/>
                        </div>
                    ) : mergedData.length > 0 ? (
                        mergedData.map(renderCategorySection)
                    ) : (
                        <Typography className={styles.emptyState}>
                            {isListsFetching ? t("Messages.loading") : t("Messages.no_content_available")}
                        </Typography>
                    )}
                </div>
            )}
        </div>
    );
};

export default FilmLists;
